import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { Notice } from '@/components/admin/Notice';
import { ErrorState, SkeletonRows } from '@/components/admin/states';
import {
  fetchIntegrationSettings,
  updateIntegrationSettings,
  type IntegrationSettingField,
} from '@/features/integrations/api';
import { ApiError } from '@/lib/api/http';

type DraftValue = string | boolean;

/**
 * 外部連携の接続設定フォーム（事務局が設定する / D-067）。
 * 秘密の項目はAPIから値が返らないため、空欄＝据え置き、「設定を消す」で環境変数に戻す。
 */
export function IntegrationSettingsBox({
  integrationId,
  onClose,
}: {
  integrationId: string;
  onClose: () => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, DraftValue>>({});
  const [cleared, setCleared] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const query = useQuery({
    queryKey: ['integration-settings', integrationId],
    queryFn: () => fetchIntegrationSettings(integrationId),
  });

  const mutation = useMutation({
    mutationFn: () => {
      const values: Record<string, string | number | boolean | null> = {};
      for (const key of cleared) values[key] = null;
      for (const [key, value] of Object.entries(draft)) {
        if (cleared.has(key)) continue;
        const field = query.data?.fields.find((f) => f.key === key);
        if (!field) continue;
        if (field.type === 'secret' && value === '') continue; // 空欄＝据え置き
        values[key] = field.type === 'number' ? Number(value) : value;
      }
      return updateIntegrationSettings(integrationId, values);
    },
    onSuccess: async () => {
      setDraft({});
      setCleared(new Set());
      setError(null);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['integration-settings', integrationId] });
      await queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
    onError: (err: unknown) => {
      setSaved(false);
      setError(err instanceof ApiError ? err.message : '保存に失敗しました。');
    },
  });

  if (query.isPending) return <SkeletonRows rows={3} />;
  if (query.isError) {
    return (
      <ErrorState message="接続設定の取得に失敗しました。" onRetry={() => void query.refetch()} />
    );
  }

  const data = query.data;

  function valueOf(field: IntegrationSettingField): DraftValue {
    const edited = draft[field.key];
    if (edited !== undefined) return edited;
    if (field.type === 'bool') return Boolean(field.value);
    if (field.type === 'secret') return '';
    return field.value === null || field.value === undefined ? '' : String(field.value);
  }

  function toggleCleared(key: string, on: boolean): void {
    setCleared((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  }

  return (
    <div>
      <p className="mb-3 text-wptext-muted">
        ここで設定した値がサーバーの環境変数より優先されます。未設定の項目は環境変数の値
        （「環境変数を使用中」と表示）が使われます。
        <strong>パスワードやトークンは保存後に画面へ表示されません</strong>
        （変更するときだけ入力してください）。
      </p>

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {saved && !error && (
        <Notice kind="success" dismissible={false}>
          保存しました。
        </Notice>
      )}

      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate();
        }}
      >
        {data.fields.map((field) => {
          const isCleared = cleared.has(field.key);
          return (
            <div key={field.key}>
              <label className="block">
                <span className="mb-1 flex flex-wrap items-center gap-2">
                  <span className="text-wptext-strong">{field.label}</span>
                  <span className="text-wptext-muted">
                    {field.source === 'app' ? '設定済み' : '環境変数を使用中'}
                  </span>
                  {field.type === 'secret' && field.is_set && (
                    <span className="rounded-sm bg-wpborder/50 px-2 py-0.5 text-wptext-muted">
                      ●●●●（保存済み）
                    </span>
                  )}
                </span>
                {field.type === 'bool' ? (
                  <input
                    type="checkbox"
                    checked={Boolean(valueOf(field))}
                    disabled={isCleared || mutation.isPending}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, [field.key]: e.target.checked }))
                    }
                  />
                ) : (
                  <input
                    type={field.type === 'secret' ? 'password' : 'text'}
                    inputMode={field.type === 'number' ? 'numeric' : undefined}
                    value={String(valueOf(field))}
                    disabled={isCleared || mutation.isPending}
                    autoComplete="off"
                    placeholder={
                      field.type === 'secret' && field.is_set ? '変更するときだけ入力' : ''
                    }
                    onChange={(e) => setDraft((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    className="w-full max-w-xl rounded-sm border border-wpborder px-3 py-2 disabled:bg-wpcanvas"
                  />
                )}
              </label>
              {field.help && <p className="mt-1 text-wptext-muted">{field.help}</p>}
              {field.source === 'app' && (
                <label className="mt-1 flex items-center gap-1 text-wptext-muted">
                  <input
                    type="checkbox"
                    checked={isCleared}
                    disabled={mutation.isPending}
                    onChange={(e) => toggleCleared(field.key, e.target.checked)}
                  />
                  設定を消して環境変数の値に戻す
                </label>
              )}
            </div>
          );
        })}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {mutation.isPending ? '保存中…' : '保存する'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
          >
            閉じる
          </button>
        </div>
      </form>
    </div>
  );
}
